import {
  Button,
  Checkbox,
  CollapsibleCard,
  ConfirmDialog,
  Select,
  TextField,
  useToast,
} from '@citizen-walk/shared-ui';
import {
  SHORT_CODE_MAX_LENGTH,
  SHORT_CODE_MIN_LENGTH,
  SHORT_LINK_DEFAULT_EXPIRY_DAYS,
  SHORT_LINK_EXPIRY_PRESET_DAYS,
  buildShortLinkUrl,
  normalizeShortCode,
  shortCodeErrorMessage,
  shortLinkRemainingDays,
  validateShortCode,
  type ShortLinkSummary,
} from '@citizen-walk/shared-types';
import type { District } from '@citizen-walk/shared-types';
import { useEffect, useMemo, useState } from 'react';
import { useSessions } from '../sessions-admin';
import { buildQrTargetLabel } from './DistrictUrlQrPanel';
import { useShortLinks, type UseShortLinksReturn } from './useShortLinks';

/**
 * 短縮アドレス (イベント配布用の期間限定アドレス、docs/26) の管理パネル。
 *
 * 目的: 会場で参加者が **PC のアドレスバーに手で打ち込む**ための短い入口を、地区管理者が自分で
 * 発行・停止・再有効化できるようにする。長い参加 URL (`/m/{urlToken}`) は QR 向けで、手入力には
 * 向かないため。発行したアドレスは `{参加者アプリの origin}/s/{アドレス}` になる。
 *
 * 中核の約束: **期限が切れても削除しなければアドレスは予約されたまま**で、期間を入れ直せば
 * 同じアドレスで再び使える (印刷物・掲示物を刷り直さなくてよい)。「削除」だけが不可逆で、
 * 削除するとそのアドレスは他の地区が取得できる状態に戻る。
 *
 * 配置は地区概要ページの「参加者用の URL と QR コード」の直下 (配布物まわりを 1 か所にまとめる)。
 *
 * 2026-10-04: 行ごとに「参加者用の URL と QR コードに短縮URLを表示する」を切り替えられる。
 * 有効期間内でチェックが入っていれば、配布対象が同じ QR パネルの URL 欄・QR 画像・印刷物に
 * 短縮アドレスが併記される (QR の中身は従来どおり長い参加 URL)。
 */

export interface DistrictShortLinkPanelProps {
  district: District;
  /** 参加者アプリの origin (未指定なら window.location.origin)。QR パネルと同じ値を渡す。 */
  participantUrlBase?: string;
  /**
   * 一覧の状態と操作。QR パネルと同じ一覧を共有するために親 (地区概要ページ) から渡す。
   * 未指定ならこのパネル単独で取得する。
   */
  shortLinks?: UseShortLinksReturn;
}

/** pure: 有効期間 select の日本語ラベル (日数だけだと長さの実感が湧かないため併記)。 */
export function formatExpiryOptionLabel(days: number): string {
  switch (days) {
    case 7:
      return '1週間（7日）';
    case 14:
      return '2週間（14日）';
    case 30:
      return '1か月（30日）';
    case 60:
      return '2か月（60日）';
    case 90:
      return '3か月（90日）';
    case 180:
      return '6か月（180日）';
    case 360:
      return '約1年（360日・最長）';
    default:
      return `${days}日`;
  }
}

/** pure: 一覧に出す状態の文言 (内部キーは出さない)。 */
export function formatShortLinkStatusText(
  link: Pick<ShortLinkSummary, 'status' | 'expiresAt'>,
  nowMs: number = Date.now(),
): string {
  switch (link.status) {
    case 'active':
      return `有効（あと${shortLinkRemainingDays(link.expiresAt, nowMs)}日）`;
    case 'expired':
      return '期限切れ（再び有効にできます）';
    case 'disabled':
      return '停止中（再び有効にできます）';
  }
}

/** pure: 日付表示 (YYYY/MM/DD)。解釈できない値は空文字。 */
export function formatShortLinkDate(iso: string | null): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const d = new Date(t);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}/${mm}/${dd}`;
}

/** pure: 折りたたみ時に見出しの下へ出す件数の要約。一覧が未取得なら null (何も出さない)。 */
export function buildShortLinkSummaryText(
  links: readonly Pick<ShortLinkSummary, 'status'>[] | null,
): string | null {
  if (!links) return null;
  if (links.length === 0) return 'まだ発行されていません';
  const active = links.filter((l) => l.status === 'active').length;
  return `発行済み ${links.length} 件（うち有効 ${active} 件）`;
}

const SHOW_ON_QR_LABEL = '参加者用の URL と QR コードに短縮URLを表示する';
const SHOW_ON_QR_HINT =
  '有効期間中だけ、配布対象が同じ「参加者用の URL と QR コード」の URL 欄・QR 画像・印刷物に、この短縮URLを併記します。';

const EXPIRY_OPTIONS = SHORT_LINK_EXPIRY_PRESET_DAYS.map((d) => ({
  value: String(d),
  label: formatExpiryOptionLabel(d),
}));

export function DistrictShortLinkPanel({
  district,
  participantUrlBase,
  shortLinks,
}: DistrictShortLinkPanelProps) {
  const { toast } = useToast();
  const ownShortLinks = useShortLinks();
  const { state, refresh, issueLink, updateLink, deleteLink } = shortLinks ?? ownShortLinks;
  const sessionsState = useSessions(district.id);
  /** 新規発行の選択肢は「今あるセッション」だけ (非表示にしたものは配らない)。 */
  const sessions = useMemo(
    () =>
      sessionsState.status === 'ready' ? sessionsState.items.filter((s) => !s.archivedAt) : [],
    [sessionsState],
  );
  /** 一覧の名前解決は非表示にしたセッションも含める (発行後に非表示にしても名前を出すため)。 */
  const allSessions = useMemo(
    () => (sessionsState.status === 'ready' ? sessionsState.items : []),
    [sessionsState],
  );

  const [targetSessionId, setTargetSessionId] = useState('');
  const [expiresInDays, setExpiresInDays] = useState(String(SHORT_LINK_DEFAULT_EXPIRY_DAYS));
  const [wantedCode, setWantedCode] = useState('');
  const [label, setLabel] = useState('');
  const [showOnQr, setShowOnQr] = useState(false);
  const [busy, setBusy] = useState(false);
  /** 行ごとの「この期間で有効にする」用の日数 (未選択の行は既定値)。 */
  const [renewDays, setRenewDays] = useState<Record<string, string>>({});
  const [deleteTarget, setDeleteTarget] = useState<ShortLinkSummary | null>(null);

  const origin = useMemo(() => {
    if (participantUrlBase) return participantUrlBase;
    if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin;
    return '';
  }, [participantUrlBase]);

  useEffect(() => {
    void refresh(district.id);
  }, [district.id, refresh]);

  // 入力中の希望アドレスは打ちながら検証する (発行ボタンを押してから怒られない)。
  // 空欄は「自動生成にする」の意味なのでエラーにしない。
  const normalizedWanted = normalizeShortCode(wantedCode);
  const wantedError = useMemo(() => {
    if (!normalizedWanted) return undefined;
    const check = validateShortCode(normalizedWanted);
    return check.ok ? undefined : shortCodeErrorMessage(check.reason);
  }, [normalizedWanted]);

  function sessionName(sessionId: string | null): string | null {
    if (!sessionId) return null;
    return allSessions.find((s) => s.id === sessionId)?.name ?? '（見つからないセッション）';
  }

  async function handleCopy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      toast('success', '短縮アドレスをコピーしました');
    } catch (e) {
      toast('error', `コピーに失敗: ${(e as Error).message}`);
    }
  }

  async function handleIssue() {
    if (wantedError) return;
    setBusy(true);
    try {
      const res = await issueLink({
        districtId: district.id,
        sessionId: targetSessionId || null,
        code: normalizedWanted || undefined,
        label: label.trim() || undefined,
        expiresInDays: Number(expiresInDays),
        showOnParticipantQr: showOnQr,
      });
      setWantedCode('');
      setLabel('');
      setShowOnQr(false);
      await refresh(district.id);
      toast('success', `短縮アドレスを発行しました: ${buildShortLinkUrl(origin, res.link.code)}`);
    } catch (e) {
      toast('error', `発行に失敗: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleRenew(link: ShortLinkSummary) {
    const days = Number(renewDays[link.code] ?? String(SHORT_LINK_DEFAULT_EXPIRY_DAYS));
    setBusy(true);
    try {
      await updateLink({ code: link.code, expiresInDays: days });
      await refresh(district.id);
      toast('success', `${formatExpiryOptionLabel(days)}の期間で有効にしました`);
    } catch (e) {
      toast('error', `変更に失敗: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleToggleDisabled(link: ShortLinkSummary) {
    setBusy(true);
    try {
      await updateLink({ code: link.code, disabled: link.status !== 'disabled' });
      await refresh(district.id);
      toast('success', link.status === 'disabled' ? '停止を解除しました' : '停止しました');
    } catch (e) {
      toast('error', `変更に失敗: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleToggleShowOnQr(link: ShortLinkSummary, next: boolean) {
    setBusy(true);
    try {
      await updateLink({ code: link.code, showOnParticipantQr: next });
      await refresh(district.id);
      toast(
        'success',
        next
          ? '参加者用の URL と QR コードに表示します'
          : '参加者用の URL と QR コードへの表示をやめました',
      );
    } catch (e) {
      toast('error', `変更に失敗: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(link: ShortLinkSummary) {
    setBusy(true);
    try {
      await deleteLink({ code: link.code });
      await refresh(district.id);
      toast('success', '短縮アドレスを削除しました');
    } catch (e) {
      toast('error', `削除に失敗: ${(e as Error).message}`);
    } finally {
      setBusy(false);
      setDeleteTarget(null);
    }
  }

  return (
    <CollapsibleCard
      title="短縮アドレス（イベント配布用・期間限定）"
      summary={buildShortLinkSummaryText(state.status === 'ready' ? state.links : null)}
      storageKey="cw-admin:district-overview:short-links"
      testId="district-short-link-panel"
    >
      <p className="text-xs text-slate-600 mb-1">
        上の参加 URL は長いため、会場の PC
        で打ち込むのは大変です。ここでは短くて打ちやすいアドレスを、期間を決めて発行できます（最長
        360 日）。掲示・チラシ・司会の読み上げに使えます。
      </p>
      <p className="text-xs text-slate-500 mb-4">
        期間が過ぎても削除しなければアドレスは取っておかれます。期間を入れ直せば
        <strong className="font-bold">同じアドレスのまま再び使えます</strong>
        （掲示物を刷り直す必要はありません）。
      </p>

      {/* 新規発行 */}
      <section className="mb-5">
        <h4 className="text-xs font-bold text-slate-600 uppercase mb-2">新しく発行する</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Select
            label="このアドレスで参加する人"
            value={targetSessionId}
            onChange={setTargetSessionId}
            options={[
              { value: '', label: '全体共通（セッションを指定しない）' },
              ...sessions.map((s) => ({ value: s.id, label: s.name })),
            ]}
            hint={
              targetSessionId
                ? 'このアドレスから参加した人は、選んだセッションが最初から入った状態になります。'
                : '参加者が自分でセッションを選びます。'
            }
          />
          <Select
            label="有効期間（今日から）"
            value={expiresInDays}
            onChange={setExpiresInDays}
            options={EXPIRY_OPTIONS}
            hint="期間が過ぎたら自動で使えなくなります（あとで入れ直せます）。"
          />
          <TextField
            label="使いたいアドレス（任意）"
            value={wantedCode}
            onChange={setWantedCode}
            placeholder="例: hakone2026"
            error={wantedError}
            hint={`半角の英小文字・数字・ハイフンで ${SHORT_CODE_MIN_LENGTH}〜${SHORT_CODE_MAX_LENGTH} 文字。空欄なら自動で短いアドレスを作ります。`}
          />
          <TextField
            label="メモ（任意）"
            value={label}
            onChange={setLabel}
            placeholder="例: 10/12 現地説明会の掲示用"
            hint="どの配布物に使ったかの覚書です（参加者には表示されません）。"
          />
        </div>
        <Checkbox
          className="mt-3"
          label={SHOW_ON_QR_LABEL}
          checked={showOnQr}
          onChange={setShowOnQr}
          hint={SHOW_ON_QR_HINT}
        />
        <p className="mt-2 text-xs text-slate-500 break-all">
          発行されるアドレス:{' '}
          <code className="text-slate-700">
            {buildShortLinkUrl(origin, normalizedWanted || '（自動で決まります）')}
          </code>
        </p>
        <Button
          type="button"
          size="sm"
          variant="primary"
          className="mt-3"
          disabled={busy || Boolean(wantedError)}
          onClick={() => void handleIssue()}
          data-testid="short-link-issue"
        >
          短縮アドレスを発行
        </Button>
      </section>

      {/* 発行済み一覧 */}
      <section>
        <h4 className="text-xs font-bold text-slate-600 uppercase mb-2">発行済みのアドレス</h4>
        {state.status === 'loading' && <p className="text-xs text-slate-500">読み込み中…</p>}
        {state.status === 'error' && (
          <p className="text-xs text-danger-700">
            一覧を取得できませんでした: {state.error.message}
          </p>
        )}
        {state.status === 'ready' && state.links.length === 0 && (
          <p className="text-xs text-slate-500">
            まだ発行されていません。上のフォームから発行できます。
          </p>
        )}
        <ul className="grid grid-cols-1 gap-3">
          {state.links.map((link) => {
            const url = buildShortLinkUrl(origin, link.code);
            const target = buildQrTargetLabel(sessionName(link.sessionId));
            return (
              <li
                key={link.code}
                className="border border-slate-200 rounded p-3"
                data-testid={`short-link-row-${link.code}`}
              >
                <div className="flex gap-2 items-start flex-wrap">
                  <code className="flex-1 basis-48 min-w-0 text-xs px-2 py-1 bg-slate-50 border border-slate-200 rounded break-all">
                    {url}
                  </code>
                  <div className="flex gap-2 shrink-0 flex-wrap">
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => void handleCopy(url)}
                    >
                      コピー
                    </Button>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center px-3 min-h-touch text-sm rounded border border-slate-300 hover:bg-slate-50 text-slate-700"
                    >
                      開く ↗
                    </a>
                  </div>
                </div>
                <p className="mt-2 text-xs text-slate-600 break-words">
                  <span
                    className={
                      link.status === 'active'
                        ? 'font-bold text-primary-700'
                        : 'font-bold text-slate-500'
                    }
                  >
                    {formatShortLinkStatusText(link)}
                  </span>
                  <span className="text-slate-400">｜</span>
                  期限 {formatShortLinkDate(link.expiresAt)}
                  <span className="text-slate-400">｜</span>
                  対象 {target}
                  <span className="text-slate-400">｜</span>
                  開かれた回数 {link.accessCount}
                </p>
                {link.label && (
                  <p className="mt-1 text-xs text-slate-500 break-words">メモ: {link.label}</p>
                )}
                <Checkbox
                  className="mt-2"
                  label={SHOW_ON_QR_LABEL}
                  checked={link.showOnParticipantQr === true}
                  onChange={(v) => void handleToggleShowOnQr(link, v)}
                  disabled={busy}
                  hint={
                    link.showOnParticipantQr && link.status !== 'active'
                      ? '有効期間外のため、いまは表示されていません（有効にすると表示されます）。'
                      : undefined
                  }
                />
                <div className="mt-2 flex gap-2 items-end flex-wrap">
                  <div className="min-w-0 w-full sm:w-auto">
                    <Select
                      label="期間を入れ直す"
                      value={renewDays[link.code] ?? String(SHORT_LINK_DEFAULT_EXPIRY_DAYS)}
                      onChange={(v) => setRenewDays((prev) => ({ ...prev, [link.code]: v }))}
                      options={EXPIRY_OPTIONS}
                    />
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="primary"
                    disabled={busy}
                    onClick={() => void handleRenew(link)}
                  >
                    この期間で有効にする
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={busy}
                    onClick={() => void handleToggleDisabled(link)}
                  >
                    {link.status === 'disabled' ? '停止を解除' : '今すぐ停止'}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="danger"
                    disabled={busy}
                    onClick={() => setDeleteTarget(link)}
                  >
                    削除
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      <ConfirmDialog
        open={deleteTarget !== null}
        intent="danger"
        title="この短縮アドレスを削除しますか？"
        message={
          <div className="text-sm text-slate-700">
            <p className="mb-2 break-all">
              <code>{deleteTarget ? buildShortLinkUrl(origin, deleteTarget.code) : ''}</code>
            </p>
            <p>
              削除すると、このアドレスは他の地区が使えるようになります。
              しばらく使わないだけなら「今すぐ停止」を選ぶと、同じアドレスのまま後で再開できます。
            </p>
          </div>
        }
        confirmLabel="削除する"
        onConfirm={() => {
          if (deleteTarget) void handleDelete(deleteTarget);
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </CollapsibleCard>
  );
}
